import { MetricLabel } from '../components/MetricHelp';
import type { AnalyticsResource } from './useAnalyticsResource';
import { formatMoment, formatNumber, formatRatio } from '../utils/periodDashboard';
import { ReportState } from './BehaviorReports';
import './FirstExperiencePanel.css';

export interface FirstExperienceData {
  period: { startDate: string; endDate: string; days: number; timeZone: string };
  observationHours: number;
  generatedAt: string;
  cohort: {
    signups: number;
    matured: number;
    immature: number;
    firstSuccess: number;
    firstSuccessRate: number | null;
  };
  timeToFirstSuccess: {
    p50Ms: number | null;
    p95Ms: number | null;
    sampleSize: number;
    coverage: {
      completedCandidateUsers: number;
      validTimestampUsers: number;
      rate: number | null;
    };
  };
  resultRendered: {
    status: 'partial' | 'unavailable';
    reason: string;
    users: number | null;
    events: number | null;
    observedRate: number | null;
    earliestRecordedAt: string | null;
  };
  limitations: string[];
}

function duration(value: number | null): string {
  if (value === null) return 'Unavailable';
  return value < 1000 ? `${Math.round(value)}ms` : `${(value / 1000).toFixed(1)}s`;
}

function CohortMetric({ label, help, value, note }: { label: string; help: string; value: string; note: string }) {
  return <article className="analytics-metric"><span><MetricLabel label={label} help={help} /></span><strong>{value}</strong><small>{note}</small></article>;
}

export default function FirstExperiencePanel({ resource, retry }: { resource: AnalyticsResource<FirstExperienceData>; retry: () => void }) {
  return <section className="first-experience" aria-labelledby="first-experience-heading">
    <div className="first-experience-heading"><h2 id="first-experience-heading">First successful experience</h2><span>24 hours after signup · HKT signup cohort</span></div>
    <ReportState {...resource} retry={retry}>{resource.data ? <>
      <div className="analytics-metrics first-experience-metrics" aria-label="First success within 24 hours">
        <CohortMetric label="Matured signups" help="Accounts whose full 24-hour observation window had elapsed when this report was generated. Only these accounts enter the first-success rate denominator." value={formatNumber(resource.data.cohort.matured)} note={`${formatNumber(resource.data.cohort.immature)} recent signups still observing`} />
        <CohortMetric label="First server success" help="Different matured accounts with a non-preview generation completed within 24 hours of signup and a valid completedAt timestamp. Backend completion is the source of truth." value={formatNumber(resource.data.cohort.firstSuccess)} note="One success per account" />
        <CohortMetric label="24-hour success rate" help="First server success divided by matured signups from the selected signup cohort. Users who signed up too recently are excluded." value={resource.data.cohort.firstSuccessRate === null ? 'Unavailable' : formatRatio(resource.data.cohort.firstSuccessRate)} note={`${formatNumber(resource.data.cohort.firstSuccess)} / ${formatNumber(resource.data.cohort.matured)} matured signups`} />
        <CohortMetric label="P95 time to success" help="95th percentile from signup to first server completion among successful matured accounts with valid timestamps. Missing historical completion timestamps do not contribute." value={duration(resource.data.timeToFirstSuccess.p95Ms)} note={`${formatNumber(resource.data.timeToFirstSuccess.sampleSize)} timed successes`} />
      </div>
      <div className="first-experience-detail">
        <div><span>Median time to success</span><strong>{duration(resource.data.timeToFirstSuccess.p50Ms)}</strong></div>
        <div><span>Completion timestamp coverage</span><strong>{formatNumber(resource.data.timeToFirstSuccess.coverage.validTimestampUsers)} / {formatNumber(resource.data.timeToFirstSuccess.coverage.completedCandidateUsers)}</strong></div>
        <div><span>Result seen in app</span><strong>{resource.data.resultRendered.users === null ? 'Unavailable' : formatNumber(resource.data.resultRendered.users)}</strong><small>{resource.data.resultRendered.observedRate === null ? resource.data.resultRendered.status : `Observed lower bound · ${formatRatio(resource.data.resultRendered.observedRate)} of matured signups`}</small></div>
      </div>
      <p className={`first-experience-status first-experience-status-${resource.data.resultRendered.status}`}>{resource.data.resultRendered.status === 'partial' ? 'Partial evidence' : 'Unavailable'}</p>
      <p className="first-experience-coverage">{resource.data.resultRendered.reason}</p>
      <details className="analytics-method"><summary>Definitions and data limits</summary><p>Updated {formatMoment(resource.data.generatedAt)} · {formatNumber(resource.data.cohort.signups)} total signups selected.</p>{resource.data.limitations.map((note) => <p key={note}>{note}</p>)}</details>
    </> : null}</ReportState>
  </section>;
}
